// Moveload Vehicle Catalog — every SA freight vehicle class Moveload can price and match.
// Grouped by class for the UI; each entry keys back to vehicle_cost_profiles.vehicle_type.
export interface VehicleSpec {
  type: string;
  capacityKg: number;
  class: VehicleClass;
  hazcapable?: boolean;
  reefer?: boolean;
  oversize?: boolean;
  prdpRequired?: boolean;
}

export type VehicleClass =
  | "passenger"
  | "courier"
  | "ldv"
  | "medium_rigid"
  | "heavy_rigid"
  | "reefer"
  | "container"
  | "tipper"
  | "flatbed"
  | "lowbed"
  | "tanker"
  | "car_carrier"
  | "livestock"
  | "removals"
  | "long_haul"
  | "cross_border";

export const VEHICLE_CATALOG: VehicleSpec[] = [
  // Passenger vehicles — anyone with a car can earn on Moveload (parcels & small cargo)
  { type: "Hatchback / Small Car",       capacityKg: 150,    class: "passenger" },
  { type: "Sedan",                       capacityKg: 250,    class: "passenger" },
  { type: "SUV / Crossover",             capacityKg: 400,    class: "passenger" },
  { type: "Minibus / People Carrier",    capacityKg: 600,    class: "passenger" },
  { type: "Motorcycle Courier",          capacityKg: 20,     class: "courier" },
  { type: "Small Bakkie (500kg)",        capacityKg: 500,    class: "ldv" },
  { type: "Bakkie 1-Ton",                capacityKg: 1000,   class: "ldv" },
  { type: "Bakkie 1-Ton Canopy",         capacityKg: 1000,   class: "ldv" },
  { type: "Double-Cab Bakkie",           capacityKg: 800,    class: "ldv" },
  { type: "Drop-Side 1.5-Ton",           capacityKg: 1500,   class: "ldv" },
  { type: "Closed Van 1.5-Ton",          capacityKg: 1500,   class: "ldv" },
  { type: "Panel Van",                   capacityKg: 1200,   class: "ldv" },
  { type: "2-Ton Truck",                 capacityKg: 2000,   class: "medium_rigid" },
  { type: "3-Ton Truck",                 capacityKg: 3000,   class: "medium_rigid" },
  { type: "4-Ton Truck",                 capacityKg: 4000,   class: "medium_rigid" },
  { type: "5-Ton Curtainsider",          capacityKg: 5000,   class: "medium_rigid" },
  { type: "6-Ton Box Truck",             capacityKg: 6000,   class: "medium_rigid" },
  { type: "8-Ton Box Truck",             capacityKg: 8000,   class: "medium_rigid" },
  { type: "8-Ton Tautliner",             capacityKg: 8000,   class: "medium_rigid" },
  { type: "10-Ton Tautliner",            capacityKg: 10000,  class: "heavy_rigid" },
  { type: "12-Ton Tautliner",            capacityKg: 12000,  class: "heavy_rigid" },
  { type: "14-Ton Tautliner",            capacityKg: 14000,  class: "heavy_rigid", prdpRequired: true },
  { type: "16-Ton Rigid",                capacityKg: 16000,  class: "heavy_rigid", prdpRequired: true },
  { type: "18-Ton Rigid",                capacityKg: 18000,  class: "heavy_rigid", prdpRequired: true },
  { type: "Refrigerated Bakkie",         capacityKg: 800,    class: "reefer", reefer: true },
  { type: "Refrigerated 3-Ton",          capacityKg: 3000,   class: "reefer", reefer: true },
  { type: "Refrigerated 6-Ton",          capacityKg: 6000,   class: "reefer", reefer: true },
  { type: "Refrigerated 8-Ton",          capacityKg: 8000,   class: "reefer", reefer: true },
  { type: "Refrigerated 10-Ton",         capacityKg: 10000,  class: "reefer", reefer: true },
  { type: "Refrigerated 14-Ton",         capacityKg: 14000,  class: "reefer", reefer: true, prdpRequired: true },
  { type: "Refrigerated 30-Ton",         capacityKg: 30000,  class: "reefer", reefer: true, prdpRequired: true },
  { type: "Container 6m (20ft)",         capacityKg: 20000,  class: "container", prdpRequired: true },
  { type: "Container 12m (40ft)",        capacityKg: 26000,  class: "container", prdpRequired: true },
  { type: "Container Truck 20ft",        capacityKg: 20000,  class: "container", prdpRequired: true },
  { type: "Container Truck 40ft",        capacityKg: 26000,  class: "container", prdpRequired: true },
  { type: "Tipper 10-Ton",               capacityKg: 10000,  class: "tipper" },
  { type: "Tipper 20-Ton",               capacityKg: 20000,  class: "tipper", prdpRequired: true },
  { type: "Sidetipper 34-Ton",           capacityKg: 34000,  class: "tipper", prdpRequired: true },
  { type: "Flatbed 8-Ton",               capacityKg: 8000,   class: "flatbed" },
  { type: "Flatbed Superlink 34-Ton",    capacityKg: 34000,  class: "flatbed", prdpRequired: true },
  { type: "Lowbed / Abnormal",           capacityKg: 60000,  class: "lowbed", oversize: true, prdpRequired: true },
  { type: "Fuel Tanker 40kL",            capacityKg: 34000,  class: "tanker", hazcapable: true, prdpRequired: true },
  { type: "Chemical Tanker 30kL",        capacityKg: 30000,  class: "tanker", hazcapable: true, prdpRequired: true },
  { type: "Water/Milk Tanker 20kL",      capacityKg: 20000,  class: "tanker", prdpRequired: true },
  { type: "Car Carrier 6-Vehicle",       capacityKg: 15000,  class: "car_carrier", prdpRequired: true },
  { type: "Livestock Truck 14-Ton",      capacityKg: 14000,  class: "livestock", prdpRequired: true },
  { type: "Furniture / Removals 8m3",    capacityKg: 2500,   class: "removals" },
  { type: "Furniture / Removals 40m3",   capacityKg: 10000,  class: "removals" },
  { type: "Long-Haul 30-Ton",            capacityKg: 30000,  class: "long_haul", prdpRequired: true },
  { type: "Long-Haul Superlink 34-Ton",  capacityKg: 34000,  class: "long_haul", prdpRequired: true },
  { type: "Long-Haul Interlink 36-Ton",  capacityKg: 36000,  class: "long_haul", prdpRequired: true },
  { type: "Cross-Border Tri-Axle",       capacityKg: 34000,  class: "cross_border", prdpRequired: true },
];

export const VEHICLE_CLASS_LABEL: Record<VehicleClass, string> = {
  passenger: "Passenger vehicles (cars)",
  courier: "Courier & motorcycle",
  ldv: "Bakkies & LDVs (≤1.5t)",
  medium_rigid: "Medium rigid (2–8t)",
  heavy_rigid: "Heavy rigid (10–18t)",
  reefer: "Refrigerated / cold-chain",
  container: "Containerised",
  tipper: "Tipper / bulk",
  flatbed: "Flatbed / deck",
  lowbed: "Lowbed / abnormal",
  tanker: "Tanker / liquid & hazchem",
  car_carrier: "Car / auto carriers",
  livestock: "Livestock",
  removals: "Furniture & removals",
  long_haul: "Long-haul superlink",
  cross_border: "Cross-border SADC",
};

export function suggestVehicleForCargo(weightKg: number, opts?: { reefer?: boolean; hazardous?: boolean; oversize?: boolean }): VehicleSpec {
  const pool = VEHICLE_CATALOG.filter(v => {
    if (opts?.reefer && !v.reefer) return false;
    if (opts?.hazardous && !v.hazcapable) return false;
    if (opts?.oversize && !v.oversize) return false;
    return v.capacityKg >= weightKg;
  });
  return (pool.sort((a, b) => a.capacityKg - b.capacityKg)[0]) ?? VEHICLE_CATALOG[VEHICLE_CATALOG.length - 1];
}

export function groupedCatalog(): Record<VehicleClass, VehicleSpec[]> {
  const out = {} as Record<VehicleClass, VehicleSpec[]>;
  for (const v of VEHICLE_CATALOG) {
    (out[v.class] ??= []).push(v);
  }
  return out;
}

// ---------------------------------------------------------------------------
// CBR v3 pricing class resolution.
// vehicle_cost_profiles is keyed by a priced vehicle_class; catalog classes that
// have no dedicated cost profile fall back to their nearest priced equivalent.
// ---------------------------------------------------------------------------
const CBR_CLASS_FALLBACK: Record<VehicleClass, string> = {
  passenger: "courier",
  courier: "courier",
  ldv: "ldv",
  medium_rigid: "medium_rigid",
  heavy_rigid: "heavy_rigid",
  long_haul: "long_haul",
  tipper: "tipper",
  reefer: "heavy_rigid",
  container: "long_haul",
  flatbed: "heavy_rigid",
  lowbed: "long_haul",
  tanker: "long_haul",
  // No car-carrier cost profile exists: the engine answers "request only" rather
  // than pricing a vehicle move as a dedicated long-haul truck.
  car_carrier: "car_carrier",
  livestock: "heavy_rigid",
  removals: "medium_rigid",
  cross_border: "long_haul",
};

/** Resolve a catalog vehicle type to the vehicle_class the CBR v3 engine prices. */
export function cbrVehicleClass(vehicleType: string): string {
  const spec = VEHICLE_CATALOG.find(v => v.type === vehicleType);
  return spec ? CBR_CLASS_FALLBACK[spec.class] : "medium_rigid";
}
